import type { ReactNode } from 'react';
import { Button, Form, OverlayTrigger, Popover } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '../../shared/DateFormatContext';

interface DateRangeFieldProps {
  dateFrom: string | null | undefined;
  dateTo: string | null | undefined;
  onChange: (next: { dateFrom: string | null; dateTo: string | null }) => void;
  projectStartDate?: string | null;
  projectEndDate?: string | null;
  disabled?: boolean;
  warning?: ReactNode;
  emptyLabel?: string;
}

/**
 * Compact trigger button + popover for editing a date-from/date-to pair inline in a table row.
 * Rendering two native date inputs directly in the cell forces the column (and the table with
 * it) to stretch far past the other columns, since `input[type=date]` has a large fixed minimum
 * width that flex/CSS can't shrink. The popover is portaled to document.body by react-bootstrap,
 * so its content never gets clipped or forces horizontal scroll on the table wrapper.
 */
export default function DateRangeField({
  dateFrom, dateTo, onChange, projectStartDate, projectEndDate, disabled, warning, emptyLabel,
}: DateRangeFieldProps) {
  const { t } = useTranslation();
  const { formatDate } = useDateFormat();

  const hasRange = !!(dateFrom || dateTo);
  const label = hasRange
    ? `${dateFrom ? formatDate(dateFrom) : '…'} – ${dateTo ? formatDate(dateTo) : '…'}`
    : (emptyLabel || t('common.wholeProject', 'Celý projekt'));
  const canPrefillFromProject = !!(projectStartDate || projectEndDate)
    && (dateFrom !== projectStartDate || dateTo !== projectEndDate);

  const popover = (
    <Popover style={{ maxWidth: 260 }} onClick={(e) => e.stopPropagation()}>
      <Popover.Body>
        <div className="d-flex flex-column gap-2" style={{ minWidth: 200 }}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">{t('common.dateFrom', 'Od')}</Form.Label>
            <Form.Control
              size="sm" type="date" value={dateFrom || ''}
              onChange={(e) => onChange({ dateFrom: e.target.value || null, dateTo: dateTo || null })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">{t('common.dateTo', 'Do')}</Form.Label>
            <Form.Control
              size="sm" type="date" value={dateTo || ''}
              onChange={(e) => onChange({ dateFrom: dateFrom || null, dateTo: e.target.value || null })}
            />
          </Form.Group>
          {warning}
          <div className="d-flex justify-content-between align-items-center gap-2 pt-1">
            {canPrefillFromProject ? (
              <Button size="sm" variant="link" className="p-0" style={{ fontSize: '0.75rem' }}
                onClick={() => onChange({ dateFrom: projectStartDate || null, dateTo: projectEndDate || null })}>
                {t('common.useProjectDates', 'Použít termín projektu')}
              </Button>
            ) : <span />}
            {hasRange && (
              <Button size="sm" variant="link" className="p-0 text-danger" style={{ fontSize: '0.75rem' }}
                onClick={() => onChange({ dateFrom: null, dateTo: null })}>
                {t('common.clear', 'Zrušit')}
              </Button>
            )}
          </div>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger trigger="click" placement="bottom-start" rootClose overlay={popover}>
      <Button
        type="button" size="sm" disabled={disabled}
        variant={hasRange ? 'outline-primary' : 'outline-secondary'}
        className="d-inline-flex align-items-center gap-1 text-nowrap"
        onClick={(e) => e.stopPropagation()}
      >
        <i className="ti ti-calendar" />
        <span>{label}</span>
      </Button>
    </OverlayTrigger>
  );
}
